import { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import api from '../../services/api';
import useAuthStore from '../../store/auth';
import { TextInput } from '../../components/FormComponents/TextInput';

const schema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type FormData = z.infer<typeof schema>;

const LoginPage = () => {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: FormData) => {
    try {
      setErrorMessage(null);
      const response = await api.post('/auth?action=login', data);
      
      // Backend wraps response in { success: true, data: { accessToken, user } }
      const loginData = response.data.data;
      
      if (!loginData || !loginData.accessToken || !loginData.user) {
        throw new Error('Missing accessToken or user in response');
      }
      
      const { accessToken, user } = loginData;
      
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      login({ user, accessToken });
      navigate('/dashboard');
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message =
        axiosError.response?.data?.message || 
        (error instanceof Error ? error.message : 'Login failed. Please check your credentials.');
      setErrorMessage(message);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stack component="form" spacing={3} onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="h4" component="h1" gutterBottom>
            Sign in
          </Typography>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <TextInput
            name="email"
            register={register}
            label="Email"
            type="email"
            fullWidth
            autoComplete="email"
            error={!!errors.email}
            helperText={errors.email?.message}
          />

          <TextInput
            name="password"
            register={register}
            label="Password"
            type="password"
            fullWidth
            autoComplete="current-password"
            error={!!errors.password}
            helperText={errors.password?.message}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            size="large"
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default LoginPage;